import type { ChangeEvent, FormEvent } from "react";
import { useId, useState } from "react";
import type { Profile, ProfileImageKind, UpdateProfilePayload } from "api";
import cn from "classnames";
import { Modal } from "components/ui/Modal";
import { Select } from "components/ui/Select";
import { IMAGE_ACCEPT, IMAGE_SPECS, imageFileError, positionOptions } from "./profile.model";

type ProfileModalProps = {
  profile: Profile;
  onSave: (payload: UpdateProfilePayload) => Promise<string | null>;
  onUploadImage: (kind: ProfileImageKind, file: File) => Promise<string | null>;
  onRemoveImage: (kind: ProfileImageKind) => Promise<string | null>;
  onClose: () => void;
};

type ImageRowProps = {
  kind: ProfileImageKind;
  url: string | null;
  onUpload: (file: File) => Promise<string | null>;
  onRemove: () => Promise<string | null>;
};

/**
 * One picture, uploaded the moment it is chosen. The text fields below wait for
 * Save, but an image has a route of its own and answers with the whole profile,
 * so there is nothing to hold back — and nothing to undo if the modal is then
 * cancelled.
 */
const ImageRow = ({ kind, url, onUpload, onRemove }: ImageRowProps) => {
  const inputId = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { noun, hint } = IMAGE_SPECS[kind];

  const run = async (action: () => Promise<string | null>) => {
    setBusy(true);
    setError(await action());
    setBusy(false);
  };

  const choose = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Picking the same file twice has to re-run the upload, so the input is
    // cleared either way — a rejected file is not left selected.
    event.target.value = "";

    if (file === undefined) return;

    const rejected = imageFileError(file);

    if (rejected !== null) {
      setError(rejected);
      return;
    }

    void run(() => onUpload(file));
  };

  const preview = kind === "avatar" ? "me-photo__p" : "me-photo__c";

  return (
    <div className="me-photo">
      <div
        className={cn(preview, { "has-img": url !== null })}
        style={kind === "cover" && url !== null ? { backgroundImage: `url(${url})` } : undefined}
      >
        {kind === "avatar" && url !== null ? <img src={url} alt="" /> : <span>{noun}</span>}
      </div>

      <div className="me-photo__a">
        {busy ? (
          <button type="button" className="pcbtn" disabled>
            Working…
          </button>
        ) : (
          <label className="pcbtn" htmlFor={inputId}>
            Upload {noun}
          </label>
        )}
        <input type="file" id={inputId} accept={IMAGE_ACCEPT} hidden onChange={choose} />

        <button type="button" className="pcbtn" disabled={busy || url === null} onClick={() => void run(onRemove)}>
          Remove
        </button>

        <p className={cn("me-hint", { "me-hint--bad": error !== null })}>{error ?? hint}</p>
      </div>
    </div>
  );
};

/**
 * The design's `#me-modal`, read as the `user` role: photo, cover and the three
 * lines of copy. The company picker beside them belongs to a QClub member, who
 * has a shared company record to point at; this account has none.
 */
export const ProfileModal = ({ profile, onSave, onUploadImage, onRemoveImage, onClose }: ProfileModalProps) => {
  const [headline, setHeadline] = useState(profile.headline ?? "");
  const [position, setPosition] = useState(profile.position ?? "");
  const [location, setLocation] = useState(profile.location ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);

    const message = await onSave({
      // `first_name` is the one field the API insists on; the rest of the
      // contact block is left out so Settings keeps owning it.
      first_name: profile.first_name ?? "",
      last_name: profile.last_name ?? "",
      headline: headline.trim() || null,
      position: position.trim() || null,
      location: location.trim() || null,
    });

    setSaving(false);

    if (message === null) {
      onClose();
      return;
    }

    setError(message);
  };

  return (
    <Modal
      title="Edit profile"
      subtitle="Your photo, cover and headline. Only you see this profile — it becomes public when you join QClub."
      wide
      onClose={onClose}
    >
      {(box) => (
        <form noValidate onSubmit={(event) => void submit(event)}>
          <ImageRow
            kind="avatar"
            url={profile.avatar_url}
            onUpload={(file) => onUploadImage("avatar", file)}
            onRemove={() => onRemoveImage("avatar")}
          />

          <ImageRow
            kind="cover"
            url={profile.cover_url}
            onUpload={(file) => onUploadImage("cover", file)}
            onRemove={() => onRemoveImage("cover")}
          />

          <div className="pc-form">
            <label className="pcf pcf--wide">
              <span>Headline</span>
              <input
                type="text"
                name="headline"
                value={headline}
                placeholder="Founder at Qubictron | SeaRates"
                maxLength={90}
                onChange={(event) => setHeadline(event.target.value)}
              />
            </label>

            <div className="pcf">
              <span>Position in company</span>
              <Select
                value={position}
                options={positionOptions(position)}
                onChange={setPosition}
                ariaLabel="Position in company"
                placeholder="Select a position"
                boundsRef={box}
              />
            </div>

            <label className="pcf">
              <span>My location</span>
              <input
                type="text"
                name="location"
                value={location}
                placeholder="Dubai, UAE"
                maxLength={120}
                onChange={(event) => setLocation(event.target.value)}
              />
            </label>
          </div>

          <div className="pc-acts">
            <button type="submit" className="pcbtn pcbtn--main" disabled={saving}>
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button type="button" className="pcbtn" onClick={onClose}>
              Cancel
            </button>
            {error !== null && <span className="pc-failed">{error}</span>}
          </div>
        </form>
      )}
    </Modal>
  );
};
